/**
 * Composes the telemetry text from the sim state and the app's measurements.
 * Called at a throttled rate; the overlay only touches the DOM on change.
 */

import { GRADE_NAMES, HZ, type Sim, STATE_NAMES } from "@lache/sim";

export interface TelemetryExtras {
  latencyMs: number;
  tickUs: number;
  frameMs: number;
  renderMs: number;
  dropped: number;
  refreshHz: number;
  steps: number;
  split: string;
  mode: string;
  pad: string;
}

export function telemetryText(sim: Sim, e: TelemetryExtras): string {
  const s = sim.state;
  const speed = Math.sqrt(s.vx * s.vx + s.vy * s.vy);
  const lines = [
    `${STATE_NAMES[s.state] ?? s.state}  tick ${s.tick}  t ${(s.tick / HZ).toFixed(2)}s  ${e.mode}`,
    `x ${s.x.toFixed(2)}  y ${s.y.toFixed(2)}  vx ${s.vx.toFixed(2)}  vy ${s.vy.toFixed(2)}  |v| ${speed.toFixed(2)}`,
    `tuck ${s.tuck.toFixed(2)}${s.tuckFloor > 0 ? ` (floor ${s.tuckFloor.toFixed(2)})` : ""}  reach ${s.reach.toFixed(2)}  pull ${s.pull.toFixed(2)}  phi ${((s.phi * 180) / Math.PI).toFixed(0)}°  facing ${s.facing > 0 ? "→" : "←"}`,
    `grounded ${s.grounded}  coyote ${s.coyote}  buffer ${s.buffer}  arms ${s.armsOut ? "out" : "in"}  assist ${s.assistBar}  lockout ${s.lockout}${s.leftRadius ? "" : "*"}`,
    s.barId >= 0
      ? `bar ${s.barId}  theta ${((s.theta * 180) / Math.PI).toFixed(1)}°  omega ${s.omega.toFixed(2)}  L ${s.L.toFixed(3)} (rest ${s.Lrest.toFixed(3)})  attach ${s.attachLeft}`
      : `bar -  last ${s.lastBarId}  impact ${s.impactSpeed.toFixed(1)}`,
    `pump ${GRADE_NAMES[s.lastPumpGrade] ?? "-"} ${s.lastPumpGrade ? `${s.lastPumpOffsetMs >= 0 ? "+" : ""}${s.lastPumpOffsetMs.toFixed(0)}ms` : ""}  catch ${GRADE_NAMES[s.lastCatchGrade] ?? "-"}  release ${GRADE_NAMES[s.lastReleaseGrade] ?? "-"}  ${e.split}`,
    `link run ${s.linkRun} (best ${s.linkBest})  attitude ${((s.phi * 180) / Math.PI).toFixed(0)}° ${s.phiDot.toFixed(2)} rad/s  I ${s.inertia.toFixed(2)}${s.freezeTicks > 0 ? `  FREEZE ${s.freezeTicks}` : ""}`,
    `latency ${e.latencyMs >= 0 ? `${e.latencyMs.toFixed(1)}ms` : "-"}  tick ${e.tickUs.toFixed(0)}us  frame ${e.frameMs.toFixed(1)}ms (${e.refreshHz.toFixed(0)}Hz)  render ${e.renderMs.toFixed(2)}ms  steps ${e.steps}  dropped ${e.dropped}`,
    `${e.pad}`,
  ];
  return lines.join("\n");
}
